import { ChangeDetectorRef, Component, ElementRef, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import swal from 'sweetalert2';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { DeviceType } from '../../../models/consumer/consumer-bulk-interaction.model';
import { ConsumerInteractionModel, ConsumerInteractionProgressType, ConsumerInteractionType } from '../../../models/consumer/consumer-interaction.model';
import { LatestTarrifModel } from '../../../models/consumer/latest-tarrif.model';
import { ConsumerInteractionService } from '../../../services/consumer-interaction.service';
import { ConsumerService } from '../../../services/consumer.service';
import { getInteractionType } from '../../../utilities/consumer-utilities';
import { ConsumerDetailInjector } from '../consumer-detail-injector';

@Component({
    selector: 'tarrif-item',
    templateUrl: './tarrif-item.component.html',
    styleUrls: ['./grid-item.scss']
})
export class TarrifItemComponent implements OnInit, OnDestroy {

    form: UntypedFormGroup;
    isSubmitted: boolean = false;
    isEditing: boolean = false;
    private numberMask = createNumberMask({
        prefix: '',
        suffix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: ',',
        allowDecimal: true,
        decimalSymbol: '.',
        decimalLimit: 4,
        integerLimit: null,
        requireDecimal: false,
        allowNegative: false,
        allowLeadingZeroes: false
    });
    @HostBinding('class.expanded') isExpanded: boolean;
    @HostBinding('class.two-thirds') isTwoThirds: boolean = false;
    @HostBinding('class.third') isThird: boolean = true;
    page: number = 1;
    consumerId: string;
    subscriptions: any[] = [];
    clientId: number;
    tariffData: LatestTarrifModel[] = [];
    tariffs: any[][] = [];
    showProgress: boolean = false;
    consumerHasBilling: boolean;
    consumerHasPayg: boolean;
    intervalList: any[] = [];
    isProcessing = false;
    progress: ConsumerInteractionProgressType = ConsumerInteractionProgressType.InProgress;
    interactionType: ConsumerInteractionType = ConsumerInteractionType.GetTarrif;

    constructor(
        private fb: UntypedFormBuilder,
        private elRef: ElementRef,
        private consumerService: ConsumerService,
        private route: ActivatedRoute,
        consumerDetailInjector: ConsumerDetailInjector,
        private consumerInteractionService: ConsumerInteractionService,
        private cdr: ChangeDetectorRef
    ) {
        this.consumerHasBilling = consumerDetailInjector.consumerHasBilling;
        this.consumerHasPayg = consumerDetailInjector.consumerHasPayg;
        this.form = fb.group({});
    }

    ngOnInit() {
        this.clientId = this.route.parent.snapshot.params['clientId'];
        this.subscriptions.push(this.route
            .params
            .subscribe(params => {
                this.consumerId = params['consumerId'];
            }));

        this.consumerService.getLatestTarrif(this.clientId, this.consumerId).then(
            result => {
                this.tariffData = result;

                if (!result) {
                    this.isThird = true; this.isTwoThirds = false;
                    return;
                } else if (result.length >= 5) {
                    this.isTwoThirds = true;
                    this.isThird = false;
                } else {
                    this.isTwoThirds = false;
                    this.isThird = true;
                }

                let splitChunk = (array: any[], chunkSize: number) => {
                    let i, j;
                    let temparray: any[][] = [];
                    for (i = 0, j = array.length; i < j; i += chunkSize) {
                        temparray.push(array.slice(i, i + chunkSize));
                    }
                    return temparray;
                }

                // split tarrifs into chunks of 5 itemed lists
                let data = result.map((x, index) => {
                    (x as any).index = index
                    return x;
                });
                this.tariffs = splitChunk(data, 5);

                this.form = this.fb.group({
                    tariffs: this.fb.array(this.tariffData.map(x => x.unitCharge.toString()))
                });
            }
        );

        if (this.consumerHasPayg && !this.consumerHasBilling) {
            this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
                let interaction = interactionRes as ConsumerInteractionModel[];

                if (!interaction) {
                    this.showProgress = false;
                } else {
                    this.showProgress = true;
                    this.progress = getInteractionType(interaction);

                    if (this.progress == ConsumerInteractionProgressType.InProgress) {
                        this.getTarrifInteraction();
                    }
                }
            }).catch((error) => {
                this.progress = ConsumerInteractionProgressType.Error;
                this.showProgress = false;
            })
        }
    }

    ngOnDestroy() {
        this.clearIntervalList();
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    getSvgId(id: number) {
        switch (id) {
            case 1:
                return '#ic-house';
            case 2:
                return '#ic-heat';
            case 3:
                return '#ic-hot-water';
            case 4:
                return '#ic-cold-water';
            case 5:
                return '#ic-chilled';
            case 6:
                return '#ic-gas';
            case 7:
                return '#ic-vrf';
            case 8:
                return '#ic-elec';
            case 9:
                return '#ic-misc';
            case 10:
                return '#ic-sewerage';
        }
    }

    onInfoClick() {
        swal({
            title: 'Current Tariffs',
            html: '<p style="text-align: left;">This widget is showing the current tariffs for the consumers consumption and standing charges. </p>\
             <p style="text-align: left;">If no information is shown here, please speak to Switch2 Customer Services on <a href="tel:03333212010" class="focus-element"><span class="focus--content">0333 321 2010</span></a></p>',
            confirmButtonText: 'Close',
            confirmButtonColor: '#00539f'
        });
    }

    private padZeros(num: string, decimalPlaces: number = 2): string {
        let zeroCount = decimalPlaces;
        let parts = num.split('.');

        if (parts.length == 1) {
            num = parts[0] + '.';
        } else {
            zeroCount = decimalPlaces - parts[1].length;
        }

        while (zeroCount > 0) {
            zeroCount--;
            num = num + '0';
        }

        return num;
    }

    onEditClick(isSubmit: boolean = false) {
        let show = () => {
            reset();
            this.isEditing = true;
            this.elRef.nativeElement.parentElement.classList.add('module__edit');
        };

        let hide = () => {
            this.isEditing = false;
            this.elRef.nativeElement.parentElement.classList.remove('module__edit');
        };

        let reset = () => {
            this.isSubmitted = false;
            this.form.enable();

            this.form = this.fb.group({
                tariffs: this.fb.array(this.tariffData.map(x => this.padZeros(x.unitCharge.toString(), 4)))
            });
        }

        // toggle/cancel
        if (!isSubmit) {
            this.isEditing = !this.isEditing;
            this.isEditing ? show() : hide();
            return;
        }

        // save
        if (isSubmit) {
            this.isSubmitted = true;
            if (this.form.invalid || !this.form.dirty) return;

            swal({
                title: "Are you sure?",
                text: "You're about to make an adjustment to a consumer's tariff. Do you want to proceed?",
                confirmButtonText: 'Yes, please',
                confirmButtonColor: '#00539f',
                showCancelButton: true,
                cancelButtonText: "No, go back",
                cancelButtonColor: '#00539f',
            }).then(val => {
                if (val.value == true) {
                    this.form.disable();

                    let observables = [];

                    this.form.value.tariffs.forEach((item, index) => {
                        let originalAmount = this.tariffData[index].unitCharge.toString();
                        if (originalAmount !== item) {
                            observables.push(this.consumerService.setTariff(this.clientId, this.consumerId, this.tariffData[index].deviceType, item));
                        }
                    });

                    forkJoin(observables).subscribe(
                        results => { },
                        errors => this.form.enable(),
                        () => hide()
                    );
                }
            });
        }
    }

    clearIntervalList() {
        for (let i = 0; i < this.intervalList.length; i++) {
            window.clearInterval(this.intervalList[i]);
        }

        for (let j = 0; j < 100; j++) {
            window.clearInterval(j);
        }

        this.intervalList = [];
    }

    getTarrifInteraction() {
        if (this.progress != ConsumerInteractionProgressType.InProgress && this.consumerHasPayg) {
            this.isProcessing = true;
            this.clearIntervalList();
            this.getTarrif();
        }

        this.intervalList.push(window.setInterval(() => { this.getTarrif() }, 30000));
    }

    getTarrif() {
        if (this.progress != ConsumerInteractionProgressType.InProgress) {
            this.consumerInteractionService.PostDeviceTarrif(this.clientId, this.consumerId, DeviceType.Other).then((deviceTarrifRes) => {

                this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
                    let interaction = interactionRes as ConsumerInteractionModel[];
                    this.isProcessing = true;
                    if (!interaction) {
                        this.showProgress = false;
                        this.isProcessing = false;
                    } else {
                        this.showProgress = true;
                        this.progress = getInteractionType(interaction);
                        if (this.progress == ConsumerInteractionProgressType.Completed || this.progress == ConsumerInteractionProgressType.Error) {
                            this.clearIntervalList();
                        }
                        this.isProcessing = false;
                    }

                }).catch((error) => {
                    this.progress = ConsumerInteractionProgressType.Error;
                    this.showProgress = false;
                    this.isProcessing = false;
                    this.clearIntervalList();
                })

            }).catch((error) => {
                this.clearIntervalList();
            })
        } else {
            if (this.consumerHasPayg) {
                this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
                    let interaction = interactionRes as ConsumerInteractionModel[];

                    if (!interaction) {
                        this.showProgress = false;
                    } else {
                        this.showProgress = true;
                        this.progress = getInteractionType(interaction);
                        if (this.progress == ConsumerInteractionProgressType.Completed || this.progress == ConsumerInteractionProgressType.Error) {
                            this.clearIntervalList();
                        }
                    }
                }).catch((error) => {
                    this.progress = ConsumerInteractionProgressType.Error;
                    this.clearIntervalList();
                    this.showProgress = false;
                })
            }
        }
    }

    toggleExpand() {
        this.isExpanded = !this.isExpanded;
    }
}
